/**
 * OrchistSearch — reusable search surface for Orchist lists.
 *
 * Phase G.7.8 — App-wide search (JL: "Pagination everywhere!!!" + the
 * follow-up "searchbar for these Orchist lists app-wide. With FAB
 * capabilities!").
 *
 * Two modes:
 *   - inline    : compact search input + filtered count display.
 *                 Designed to live at the top of LineageList. Filters
 *                 the rows currently loaded (paginated upstream).
 *   - overlay   : fullscreen search drawer triggered by the FAB.
 *                 Same filter logic, larger surface, results list inline.
 *
 * Search semantics (client-side at MVP scale):
 *   matches the query (lowercase contains) against any of:
 *     - publicIdentityString
 *     - viewer's private nickname for the Orchist
 *     - viewer's notes on the Orchist
 *     - acknowledgedAt formatted as YYYY-MM / YYYY-MM-DD
 *   Multiple terms (whitespace-separated) require ALL to match.
 *
 * @scale-marker stage-b — client-side contains filter is fine through
 *   ~10k loaded rows. At ~100k, switch to server-side full-text via
 *   Postgres tsvector on publicIdentityString. Sovereign Stewardship
 *   will email when threshold approaches; see
 *   sacred/scale-stewardship.md#stage-b.
 */

import { useMemo } from 'react';
import { resolveTimezone, utcToLocalDateString } from '@/lib/dates';
import { useAuth } from '@/contexts/AuthContext';
import {
  lineageDisplayString,
  isFirstOrchist,
  type LineageRow,
} from '@/hooks/useLineage';
import { Glyph } from '@/components/Glyph';
import type { NicknameRow } from '@/hooks/useNicknames';
import type { EmailShareRow } from '@/hooks/useEmailShares';
import { evaluateQuery, parseQuery } from '@/lib/fuzzy-search';

// -----------------------------------------------------------------------------
// Pure filter logic — exported so consumers can reuse outside React tree.
// -----------------------------------------------------------------------------

/**
 * Build the haystack string for an Orchist row + optional nickname + optional
 * shared email (when the viewer has been given that Orchist's email). Joined
 * with whitespace so the FAB engine can word-split for fuzzy matching.
 */
function buildHaystack(
  row: LineageRow,
  nickname: NicknameRow | null,
  emailShare: EmailShareRow | null,
  viewerTz: string
): string {
  return [
    lineageDisplayString(row, viewerTz),
    row.publicIdentityString ?? '',
    nickname?.nickname ?? '',
    nickname?.notes ?? '',
    emailShare?.sharedEmail ?? '',
    utcToLocalDateString(row.invitationAcceptedAt ?? row.invitationConfirmedAt, viewerTz) ?? '',
    isFirstOrchist(row) ? 'founding self-recognized' : '',
  ].join(' \n ');
}

/**
 * The real FAB matcher: Fuzzy + Boolean.
 *   - Whitespace = AND (default)
 *   - `OR` keyword groups adjacent terms
 *   - `-term` or `NOT term` excludes
 *   - `"phrase"` is exact substring (no fuzz)
 *   - Bare terms ≥ 4 chars use Levenshtein with tolerance 1–2 (typo-friendly)
 *
 * Searched fields: public name, private nickname, nickname notes, shared email
 * (visible to viewer only), formatted acknowledgment date, founding marker.
 *
 * Implementation lives in lib/fuzzy-search.ts; this is the per-row adapter.
 */
export function matchesOrchistQuery(
  row: LineageRow,
  nickname: NicknameRow | null,
  rawQuery: string,
  viewerTz: string,
  emailShare: EmailShareRow | null = null
): boolean {
  if (!rawQuery.trim()) return true;
  const haystack = buildHaystack(row, nickname, emailShare, viewerTz);
  return evaluateQuery(haystack, parseQuery(rawQuery));
}

export interface OrchistSearchInputProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  ariaLabel?: string;
  /** Optional summary line under the input ("Showing N of M") */
  summary?: string;
}

/** Standalone search input. Caller owns the value + filtering. */
export function OrchistSearchInput({
  value,
  onChange,
  placeholder = 'Search The Line...',
  autoFocus = false,
  ariaLabel = 'Search Orchists',
  summary,
}: OrchistSearchInputProps) {
  return (
    <div className="space-y-2">
      <div className="relative">
        <input
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label={ariaLabel}
          autoFocus={autoFocus}
          className="w-full px-4 py-3 pl-10 border border-ink/15 bg-paper/60 text-sm font-display placeholder:text-ink/30 focus:outline-none focus:border-ink/50 transition-colors"
        />
        <span
          aria-hidden
          className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/30 text-xs"
        >
          ⌕
        </span>
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ink/70 transition-colors text-xs"
          >
            ✕
          </button>
        )}
      </div>
      {summary && (
        <p className="text-[0.7rem] text-ink/45 italic font-display text-center">
          {summary}
        </p>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Search result row (used inside the FAB overlay)
// -----------------------------------------------------------------------------

export function OrchistSearchResultRow({
  row,
  nickname,
  emailShare,
  viewerTz,
  onPick,
}: {
  row: LineageRow;
  nickname: NicknameRow | null;
  emailShare?: EmailShareRow | null;
  viewerTz: string;
  onPick?: (row: LineageRow) => void;
}) {
  const display = lineageDisplayString(row, viewerTz);
  const date = utcToLocalDateString(row.invitationAcceptedAt ?? row.invitationConfirmedAt, viewerTz);

  const inner = (
    <div className="flex items-start gap-3 py-2">
      {row.glyphSeed && (
        <div className="flex-shrink-0 text-ink/75 mt-0.5">
          <Glyph seed={row.glyphSeed} size={36} label={`${display}'s sigil`} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="display text-sm md:text-base truncate">{display}</p>
        {nickname && (
          <p className="text-xs text-accent italic font-display truncate">
            you call them: <span className="text-ink not-italic">{nickname.nickname}</span>
          </p>
        )}
        {emailShare && (
          <p className="text-[0.65rem] text-ink/55 font-mono truncate">
            {emailShare.sharedEmail}
          </p>
        )}
        {date && (
          <p className="text-[0.65rem] text-ink/40 tracking-wide">
            Acknowledged {date}
          </p>
        )}
      </div>
    </div>
  );

  if (onPick) {
    return (
      <button
        type="button"
        onClick={() => onPick(row)}
        className="w-full text-left border border-ink/10 px-3 hover:border-ink/40 hover:bg-paper/80 transition-colors"
      >
        {inner}
      </button>
    );
  }
  return <div className="border border-ink/10 px-3">{inner}</div>;
}

// -----------------------------------------------------------------------------
// useOrchistSearchResults — convenience hook for callers
// -----------------------------------------------------------------------------

export function useOrchistSearchResults(
  rows: LineageRow[],
  nicknamesByTargetId: Map<string, NicknameRow>,
  query: string,
  emailsByOwnerId?: Map<string, EmailShareRow>
): { results: LineageRow[]; matched: number; total: number } {
  const { contact } = useAuth();
  const viewerTz = resolveTimezone(contact?.timezone);

  const results = useMemo(() => {
    if (!query.trim()) return rows;
    return rows.filter((r) =>
      matchesOrchistQuery(
        r,
        nicknamesByTargetId.get(r.id) ?? null,
        query,
        viewerTz,
        emailsByOwnerId?.get(r.id) ?? null
      )
    );
  }, [rows, nicknamesByTargetId, query, viewerTz, emailsByOwnerId]);

  return { results, matched: results.length, total: rows.length };
}
